import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import EmployeeLayout from "@/components/EmployeeLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import TestsList from "@/pages/TestsList";
import TestForm from "@/pages/TestForm";
import Certificate from "@/pages/Certificate";
import Orders from "@/pages/Orders";
import Employees from "@/pages/Employees";
import Import from "@/pages/Import";
import Settings from "@/pages/Settings";
import EmployeeHome from "@/pages/EmployeeHome";
import GoogleGate from "@/pages/GoogleGate";
import GoogleCallback from "@/pages/GoogleCallback";
import AdminUsers from "@/pages/AdminUsers";
import AdminRoles from "@/pages/AdminRoles";
import AuditTrail from "@/pages/AuditTrail";
import { Toaster } from "sonner";

// Top-level chooser: send admins/testers/viewers to the staff Layout, employees
// to the simplified EmployeeLayout.
function ChromePicker() {
  const { kind } = useAuth();
  return kind === "employee" ? <EmployeeLayout /> : <Layout />;
}

// Decides which tier the user is on. /auth/google is always reachable (it
// establishes the tier-1 session). Without tier-1, only the Google gate shows.
function AppRoutes() {
  const { companyAuthed, loading } = useAuth();

  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center bg-[#f5f1ec]"
      >
        <div className="text-xs tracking-[0.3em] uppercase text-[#50493b]">
          Verifying access…
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth/google" element={<GoogleCallback />} />
      {companyAuthed ? (
        <>
          <Route path="/login" element={<Login />} />
          <Route
            element={
              <ProtectedRoute>
                <ChromePicker />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tests" element={<TestsList />} />
            <Route path="/tests/new" element={<TestForm />} />
            <Route path="/tests/:id" element={<TestForm />} />
            <Route path="/tests/:id/certificate" element={<Certificate />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/import" element={<Import />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/roles" element={<AdminRoles />} />
            <Route path="/admin/audit" element={<AuditTrail />} />
            <Route path="/me" element={<EmployeeHome />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </>
      ) : (
        <Route path="*" element={<GoogleGate />} />
      )}
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <AppRoutes />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
